import { OrganizationDashboardSettingPage } from './OrganizationDashboardSettingPage'
import { AccountingSettingsPage } from '@/pages/AccountingSettingsPage'
import { ConnectionsListPage } from '@/pages/ConnectionsListPage'
import { UnmatchedItemsPage } from '@/pages/UnmatchedItemsPage'
import { ConnectionDetailPage } from '@/pages/ConnectionDetailPage'
import { CustomFieldsPage } from '@/pages/CustomFieldsPage'
import { EmailIdentitySettingsPage } from '@/pages/EmailIdentitySettingsPage'
import { FinanceSettingsPage } from '@/pages/FinanceSettingsPage'
import {
  DiscountCodeSettingsView,
  type DiscountCodeSettingsViewState,
} from '@/pages/DiscountCodeSettingsView'
import { MembershipLevelsPage } from '@/pages/MembershipLevelsPage'
import { PaymentSettingsPage } from '@/pages/PaymentSettingsPage'
import { RenewalSettingsPage } from '@/pages/RenewalSettingsPage'
import { RenewalArmingReviewPage } from '@/pages/RenewalArmingReviewPage'
import type { ContactsView } from '@/pages/StaffContactsView.types'
import { TaxSettingsPage } from '@/pages/TaxSettingsPage'
import { SystemEmailTemplatesPage } from '@/pages/SystemEmailTemplatesPage'
import { SystemEmailSettingsPage } from '@/pages/SystemEmailSettingsPage'
import { EngagementWeightsPage } from '@/pages/EngagementWeightsPage'
import {
  AdminDirectoryPreviewPage,
  AdminDirectorySettingsPage,
} from '@/pages/AdminDirectorySettingsPage'
import { SeatsPage } from '@/pages/SeatsPage'
import { JoinFormSettingsPage } from '@/pages/JoinFormSettingsPage'
import { CreditUnitSettingsPage } from '@/pages/CreditUnitSettingsPage'
import type { StaffCapabilities } from '@/pages/staffCapabilities'

export type SettingsViewState =
  | { kind: 'custom-fields' }
  | { kind: 'membership-levels' }
  | { kind: 'renewal-settings' }
  | { kind: 'renewal-arming-review' }
  | { kind: 'payment-settings' }
  | { kind: 'finance-settings' }
  | { kind: 'tax-settings' }
  | DiscountCodeSettingsViewState
  | { kind: 'accounting-settings' }
  | { kind: 'connections' }
  | { kind: 'unmatched-items' }
  | { kind: 'connection-detail'; connectionId: number }
  | { kind: 'email-identity-settings' }
  | { kind: 'system-email-settings' }
  | { kind: 'system-email-templates' }
  | { kind: 'engagement-weights' }
  | { kind: 'admin-directory-settings' }
  | { kind: 'admin-directory-preview' }
  | { kind: 'seats' }
  | { kind: 'join-form-settings' }
  | { kind: 'organization-dashboard-setting' }
  | { kind: 'credit-unit-settings' }

export function isSettingsView(view: { kind: string }): view is SettingsViewState {
  return settingsViewKinds.has(view.kind)
}

// This dispatcher is intentionally exhaustive: each settings surface has distinct props.
// eslint-disable-next-line complexity
export function StaffSettingsView({
  view,
  navigateTo,
  currentAssociationDate,
  onOpenMember,
  viewOnly = false,
  viewerEmail,
  capabilities = {},
}: {
  view: SettingsViewState
  navigateTo: (view: ContactsView, path: string) => void
  currentAssociationDate?: string
  onOpenMember: (personId: number) => void
  viewOnly?: boolean
  viewerEmail?: string
  capabilities?: StaffCapabilities
}) {
  switch (view.kind) {
    case 'custom-fields':
      return <CustomFieldsPage />
    case 'membership-levels':
      return <MembershipLevelsPage />
    case 'renewal-settings':
      return (
        <RenewalSettingsPage
          onReviewArming={() =>
            navigateTo({ kind: 'renewal-arming-review' }, '/settings/renewals/arming-review')
          }
        />
      )
    case 'renewal-arming-review':
      return (
        <RenewalArmingReviewPage
          currentAssociationDate={currentAssociationDate}
          onCancel={() => navigateTo({ kind: 'renewal-settings' }, '/settings/renewals')}
          onArmed={() => navigateTo({ kind: 'renewal-settings' }, '/settings/renewals')}
          onOpenMember={onOpenMember}
        />
      )
    case 'payment-settings':
      return <PaymentSettingsPage />
    case 'finance-settings':
      return <FinanceSettingsPage />
    case 'tax-settings':
      return <TaxSettingsPage />
    case 'discount-codes':
    case 'discount-code-detail':
      return <DiscountCodeSettingsView view={view} navigateTo={navigateTo} />
    case 'accounting-settings':
      return <AccountingSettingsPage />
    case 'connections':
      return <ConnectionsListPage />
    case 'unmatched-items':
      return <UnmatchedItemsPage canViewConnections={capabilities.connections?.view} />
    case 'connection-detail':
      return <ConnectionDetailPage connectionId={view.connectionId} />
    case 'email-identity-settings':
      return <EmailIdentitySettingsPage />
    case 'system-email-settings':
      return <SystemEmailSettingsPage />
    case 'system-email-templates':
      return <SystemEmailTemplatesPage />
    case 'engagement-weights':
      return <EngagementWeightsPage />
    case 'admin-directory-settings':
      return <AdminDirectorySettingsPage />
    case 'admin-directory-preview':
      return <AdminDirectoryPreviewPage />
    case 'seats':
      return <SeatsPage viewOnly={viewOnly} viewerEmail={viewerEmail} />
    case 'join-form-settings':
      return <JoinFormSettingsPage />
    case 'organization-dashboard-setting':
      return <OrganizationDashboardSettingPage />
    case 'credit-unit-settings':
      return <CreditUnitSettingsPage capabilities={capabilities} />
  }
}

const settingsViewKinds: ReadonlySet<string> = new Set([
  'custom-fields',
  'membership-levels',
  'renewal-settings',
  'renewal-arming-review',
  'payment-settings',
  'finance-settings',
  'tax-settings',
  'discount-codes',
  'discount-code-detail',
  'accounting-settings',
  'connections',
  'unmatched-items',
  'connection-detail',
  'email-identity-settings',
  'system-email-settings',
  'system-email-templates',
  'engagement-weights',
  'admin-directory-settings',
  'admin-directory-preview',
  'seats',
  'join-form-settings',
  'organization-dashboard-setting',
  'credit-unit-settings',
])
